import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  FormAlert,
  Input,
  Skeleton,
  useToast,
} from '@mgdlead/ui';
import { Bell, Mail, MessageCircle, Send } from 'lucide-react';
import { can } from '@mgdlead/shared';

import { useWorkspace } from '../workspace/WorkspaceContext';
import { useAlertSettings, useSendTestAlert, useUpdateAlertSettings } from '../../lib/api';
import { humanizeError } from '../../lib/errors';

/**
 * Para onde vai o aviso quando um número cai (pedido do dono, 06/10).
 *
 * Três canais, e a tela diz o estado de cada um em vez de prometer: painel (sempre),
 * e-mail (dono e administradores) e WhatsApp (o número que a pessoa cadastrar aqui).
 */
export function AlertsSection() {
  const { workspace, role } = useWorkspace();
  const editable = can(role, 'workspace:update');
  const settings = useAlertSettings(workspace.id);
  const update = useUpdateAlertSettings();
  const sendTest = useSendTestAlert();
  const { toast } = useToast();
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const saved = settings.data?.whatsappE164 ?? null;
  const savedEmail = settings.data?.email ?? null;
  useEffect(() => {
    setPhone(saved ?? '');
  }, [saved]);
  useEffect(() => {
    setEmail(savedEmail ?? '');
  }, [savedEmail]);

  if (settings.isPending) {
    return (
      <Card className="flex flex-col gap-3">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-24 w-full" />
      </Card>
    );
  }
  if (settings.isError || !settings.data) {
    return <FormAlert>{humanizeError(settings.error)}</FormAlert>;
  }
  const data = settings.data;

  const save = () => {
    setError(null);
    update.mutate(
      {
        workspaceId: workspace.id,
        whatsapp: phone.trim() === '' ? null : phone,
        email: email.trim() === '' ? null : email,
      },
      {
        onSuccess: (r) =>
          toast({
            title: 'Avisos salvos',
            description: [
              r.email ? `E-mail: ${r.email}` : 'E-mail: dono e administradores',
              r.whatsappE164 ? `WhatsApp: ${r.whatsappE164}` : 'WhatsApp: desligado',
            ].join(' · '),
            tone: 'success',
          }),
        onError: (e) => setError(humanizeError(e)),
      },
    );
  };

  const test = () => {
    setError(null);
    sendTest.mutate(
      { workspaceId: workspace.id },
      {
        onSuccess: (r) =>
          toast({
            title: 'Mensagem de teste enviada',
            description: `Confira o WhatsApp ${r.sentTo}.`,
            tone: 'success',
          }),
        onError: (e) => setError(humanizeError(e)),
      },
    );
  };

  return (
    <Card className="flex flex-col gap-4">
      <CardHeader>
        <CardTitle>Avisos de número fora do ar</CardTitle>
        <CardDescription>
          Quando um número monitorado cai, as entradas e saídas dos grupos dele param de ser
          registradas. O aviso sai na hora — e outro quando ele voltar.
        </CardDescription>
      </CardHeader>

      <ul className="flex flex-col gap-3 text-sm">
        <li className="flex items-start gap-3">
          <Bell aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <span>
            <strong className="font-medium text-foreground">No painel</strong>
            <span className="block text-muted-foreground">
              Faixa vermelha no topo de todas as telas e o sino de incidentes. Sempre ligado.
            </span>
          </span>
        </li>
        <li className="flex items-start gap-3">
          <Mail aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <span>
            <strong className="font-medium text-foreground">Por e-mail</strong>
            <span className="block text-muted-foreground">
              {data.email
                ? `Para ${data.email}.`
                : data.emails.length > 0
                  ? `Para ${data.emails.join(', ')} (dono e administradores). Cadastre outro abaixo, se preferir.`
                  : 'Ninguém com e-mail entre o dono e os administradores — cadastre um abaixo.'}
            </span>
          </span>
        </li>
        <li className="flex items-start gap-3">
          <MessageCircle
            aria-hidden="true"
            className="mt-0.5 size-4 shrink-0 text-muted-foreground"
          />
          <span>
            <strong className="font-medium text-foreground">Pelo WhatsApp</strong>
            <span className="block text-muted-foreground">
              {saved
                ? `Para ${saved}.`
                : 'Desligado — cadastre abaixo o celular que deve receber o aviso.'}
            </span>
          </span>
        </li>
      </ul>

      {!data.whatsappChannelReady ? (
        <FormAlert tone="warning">
          O número de alertas do MGDLead ainda não está no ar: por enquanto o aviso chega pelo
          painel e por e-mail. O seu WhatsApp fica salvo e passa a receber assim que ele ligar.
        </FormAlert>
      ) : null}

      {error ? <FormAlert>{error}</FormAlert> : null}

      <div className="flex max-w-md flex-col gap-3">
        <Field
          label="E-mail para receber os avisos"
          hint="Deixe vazio para mandar ao dono e aos administradores do workspace."
          disabled={!editable}
        >
          {(control) => (
            <Input
              {...control}
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              disabled={!editable}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>
        <Field
          label="WhatsApp para receber os avisos"
          hint="Com DDD, ex.: (17) 99123-4567. Deixe vazio para desligar."
          disabled={!editable}
        >
          {(control) => (
            <Input
              {...control}
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              disabled={!editable}
              onChange={(event) => setPhone(event.target.value)}
            />
          )}
        </Field>
        {editable ? (
          <div className="flex flex-col gap-2 md:flex-row">
            <Button
              variant="primary"
              block
              className="md:w-auto"
              loading={update.isPending}
              loadingLabel="Salvando"
              onClick={save}
            >
              Salvar
            </Button>
            <Button
              variant="secondary"
              block
              className="md:w-auto"
              leadingIcon={<Send />}
              disabled={!saved || !data.whatsappChannelReady}
              loading={sendTest.isPending}
              loadingLabel="Enviando"
              onClick={test}
            >
              Mandar mensagem de teste
            </Button>
          </div>
        ) : null}
      </div>

      {data.recent.length > 0 ? (
        <div className="flex flex-col gap-1 border-t border-border pt-3 text-sm">
          <span className="font-medium text-foreground">Últimos avisos</span>
          {data.recent.map((a, i) => (
            <span key={i} className="text-muted-foreground">
              {new Date(a.createdAt).toLocaleString('pt-BR', {
                timeZone: workspace.timezone,
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}{' '}
              · {a.monitorName} {a.kind === 'DOWN' ? 'caiu' : 'voltou'} ·{' '}
              {a.channel === 'EMAIL' ? 'e-mail' : 'WhatsApp'}
              {a.status !== 'SENT' ? ` · não enviado (${a.detail ?? 'erro'})` : ''}
            </span>
          ))}
        </div>
      ) : null}
    </Card>
  );
}
